import { useState, useEffect } from 'react'
import { AssessmentQuestion } from '../../types/assessment'

interface QuestionDisplayProps {
  question: AssessmentQuestion
  onSubmit: (selectedOptions: string[]) => void
  isSubmitting: boolean
}

export function QuestionDisplay({ question, onSubmit, isSubmitting }: QuestionDisplayProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const isMultipleChoice = question.question_type === 'multiple_choice'

  // Reset selection when question changes
  useEffect(() => {
    setSelectedOptions([])
  }, [question.id])

  const handleOptionClick = (optionId: string) => {
    if (isSubmitting) return

    if (isMultipleChoice) {
      // Toggle selection for multiple choice
      setSelectedOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
      )
    } else {
      // Single selection
      setSelectedOptions([optionId])
    }
  }

  const handleSubmit = () => {
    if (selectedOptions.length > 0 && !isSubmitting) {
      onSubmit(selectedOptions)
    }
  }

  return (
    <div className="space-y-6">
      {/* Question number */}
      <div className="text-sm text-gray-500 font-medium">
        Question {question.sequence_number}
      </div>

      {/* Question content */}
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-900 font-medium">{question.content}</p>
      </div>

      {/* Code snippet if present */}
      {question.code_snippet && (
        <div className="rounded-lg overflow-hidden border border-gray-200">
          <div className="bg-gray-800 px-4 py-2 text-sm text-gray-300 flex justify-between items-center">
            <span>{question.code_language || 'Code'}</span>
          </div>
          <pre className="bg-gray-900 p-4 overflow-x-auto">
            <code className="text-sm text-gray-100 font-mono whitespace-pre">
              {question.code_snippet}
            </code>
          </pre>
        </div>
      )}

      {/* Instructions */}
      <p className="text-sm text-gray-500">
        {isMultipleChoice
          ? 'Select all that apply:'
          : 'Select one answer:'}
      </p>

      {/* Options */}
      <div className="space-y-3">
        {question.options
          .sort((a, b) => a.display_order - b.display_order)
          .map((option) => {
            const isSelected = selectedOptions.includes(option.id)

            return (
              <button
                key={option.id}
                onClick={() => handleOptionClick(option.id)}
                disabled={isSubmitting}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-start gap-3">
                  {/* Selection indicator */}
                  <div
                    className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-${
                      isMultipleChoice ? 'md' : 'full'
                    } border-2 flex items-center justify-center ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Option content */}
                  <div className="flex-1">
                    {option.option_code ? (
                      <pre className="font-mono text-sm bg-gray-100 p-2 rounded overflow-x-auto">
                        <code>{option.option_code}</code>
                      </pre>
                    ) : (
                      <span className="text-gray-900">{option.option_text}</span>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
      </div>

      {/* Submit button */}
      <div className="flex justify-end pt-4">
        <button
          onClick={handleSubmit}
          disabled={selectedOptions.length === 0 || isSubmitting}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            selectedOptions.length > 0 && !isSubmitting
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Answer'
          )}
        </button>
      </div>
    </div>
  )
}
